import { useState, useEffect } from "react"
import { useAppSelector } from "../../app/hooks"
import type { RootState } from "../../app/store/store"
import { ProfileImage } from "../ProfileImage"
import { shareFeedback, updateRecruiterMatchAction } from "../../features/jobs/jobAPI"
import { toast } from "sonner"

interface PersonWithAccess {
    email: string
    name?: string
    role: string
}

interface ProfileHeaderProps {
    activeTab: string
    setActiveTab: (tab: string) => void
    candidate: any
    jobId?: number | null
    matchId?: number | null
    stage?: string | null
    status?: string | null
    showTabs?: boolean
}

export const ProfileHeader = ({ activeTab, setActiveTab, candidate, jobId, matchId, stage = null, status = null, showTabs = true }: ProfileHeaderProps) => {
    const accessToken = useAppSelector((state: RootState) => state.auth.accessToken)
    const userGroups = useAppSelector((state: RootState) => state.auth.user?.groups ?? [])
    const [isShareModalOpen, setIsShareModalOpen] = useState(false)
    const [addPeopleInput, setAddPeopleInput] = useState("")
    const [emailError, setEmailError] = useState<string>("")
    const [peopleWithAccess, setPeopleWithAccess] = useState<PersonWithAccess[]>([])
    const [isAwaitingReviewSubmitting, setIsAwaitingReviewSubmitting] = useState(false)
    const [isSharingFeedback, setIsSharingFeedback] = useState(false)
    const [localRecruiterStatus, setLocalRecruiterStatus] = useState<string | null>(null)

    const shareTitle = [candidate.role, candidate.name].filter(Boolean).join(" ") || candidate.name || "Profile"
    const normalizedStage = (stage ?? "").trim()
    const isCompanyRecruiter = userGroups.includes("company_recruiter")
    const isCompanyHiringManager = userGroups.includes("company_hiring_manager")
    const normalizedStatus = (localRecruiterStatus ?? status ?? "")
        .trim()
        .toLowerCase()
        .replace(/_/g, " ")
    const isAwaitingReviewStatus = normalizedStatus === "awaiting review"
    const showAwaitingReviewButton =
        normalizedStage === "In review" &&
        !(isCompanyRecruiter && isAwaitingReviewStatus)
    const showFeedbackTab =
        normalizedStage === "In review" ||
        normalizedStage === "Shortlisted" ||
        normalizedStage === "Verdict"

    useEffect(() => {
        setLocalRecruiterStatus(null)
    }, [matchId, status])

    const isValidEmail = (value: string): boolean => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    }

    const handleAwaitingReviewButtonClick = () => {
        if (isCompanyHiringManager && !isCompanyRecruiter) {
            setIsShareModalOpen(true)
            return
        }
        void handleAwaitingReviewClick()
    }

    const handleAwaitingReviewClick = async () => {
        if (!accessToken || !matchId) return
        try {
            setIsAwaitingReviewSubmitting(true)
            const result = await updateRecruiterMatchAction(
                matchId,
                { stage: "In review", status: "Awaiting review" },
                accessToken
            )
            if (result.error) {
                toast.error(result.error)
            } else {
                setLocalRecruiterStatus("Awaiting review")
                toast.success("Updated to Awaiting review.")
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to update."
            toast.error(message)
        } finally {
            setIsAwaitingReviewSubmitting(false)
        }
    }

    const handleAddPerson = () => {
        const raw = addPeopleInput.trim()
        if (!raw) return

        if (!isValidEmail(raw)) {
            setEmailError("Please enter a valid email address.")
            return
        }

        const email = raw.toLowerCase()
        if (peopleWithAccess.some((p) => p.email.toLowerCase() === email)) {
            setEmailError("This email has already been added.")
            return
        }

        const localPart = email.split("@")[0] || ""
        const name = localPart ? localPart.charAt(0).toUpperCase() + localPart.slice(1) : email
        setPeopleWithAccess((prev) => [...prev, { email, name, role: "Recruiter" }])
        setAddPeopleInput("")
        setEmailError("")
    }

    const handleRemovePerson = (emailToRemove: string) => {
        setPeopleWithAccess((prev) => prev.filter((p) => p.email !== emailToRemove))
    }

    const closeShareModal = () => {
        setIsShareModalOpen(false)
        setAddPeopleInput("")
        setEmailError("")
        setPeopleWithAccess([])
    }

    const handleShareSave = async () => {
        if (!accessToken || !jobId || !candidate?.id) {
            toast.error("Missing required information to share feedback.")
            return
        }

        if (peopleWithAccess.length === 0) {
            setEmailError("Please add at least one email address.")
            return
        }

        try {
            setIsSharingFeedback(true)
            const result = await shareFeedback(
                {
                    job_post: jobId,
                    candidate: candidate.id,
                    email_ids: peopleWithAccess.map((person) => person.email),
                },
                accessToken
            )

            if (result.error) {
                toast.error(result.error)
                return
            }

            toast.success("Feedback shared successfully.")
            closeShareModal()
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to share feedback."
            toast.error(message)
        } finally {
            setIsSharingFeedback(false)
        }
    }

    const getInitial = (person: PersonWithAccess) => {
        if (person.name && person.name.length === 1) return person.name
        const part = person.email.split("@")[0]
        return part ? part.charAt(0).toUpperCase() : "?"
    }

    const profilePicture = candidate.profile_picture ?? candidate.avatar ?? null
    const matchScore = Number.parseFloat(String(candidate.score ?? 0))
    const scorePercentage = Number.isFinite(matchScore) ? matchScore : 0
    const scoreStrokePercentage = Math.min(Math.max(scorePercentage, 0), 100)

    const tabButtonClass = (isActive: boolean) =>
        `rounded-md px-6 py-2 text-small font-normal transition-all ${
            isActive
                ? "bg-[#E8E9EB] text-[#3C424F]"
                : "border border-gray-300 bg-white text-[#3C424F] hover:bg-gray-50"
        }`

    return (
        <div className="mb-8 rounded-md bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                <div className="flex gap-6">
                    <div className="h-24 w-24 shrink-0 overflow-hidden rounded-md border-2 border-black shadow-sm md:h-28 md:w-28">
                        <ProfileImage
                            profilePicture={profilePicture}
                            accessToken={accessToken || undefined}
                            alt={candidate.name || "Candidate"}
                            className="h-full w-full object-cover"
                            fallbackPath="/assets/images/Avatar2.jpg"
                        />
                    </div>
                    <div className="flex flex-col">
                        <h1 className="text-title font-medium text-black md:text-3xl">{candidate.name}</h1>
                        <p className="text-paragraph font-normal text-gray-600">{candidate.role}</p>
                        <p className="text-paragraph text-gray-400">{candidate.experience}</p>
                        <p className="text-paragraph text-gray-400">{candidate.location}</p>
                    </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                    {showAwaitingReviewButton && (
                        <button
                            type="button"
                            onClick={handleAwaitingReviewButtonClick}
                            disabled={isAwaitingReviewSubmitting || (isCompanyRecruiter && !matchId)}
                            className="flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16.7705 6.84356L14.1795 9.43454M15.7575 22.751C14.8051 22.751 13.9574 22.1116 13.6958 21.1961L11.1645 12.3365L2.30491 9.80517C1.38936 9.54358 0.75 8.69592 0.75 7.74381C0.75 6.77829 1.37112 5.94349 2.29563 5.66612C4.19779 5.09547 13.5008 2.47811 19.2791 0.856073C21.3325 0.279662 23.2213 2.16845 22.6449 4.22184C21.0229 10.0002 18.4055 19.3032 17.8348 21.2054C17.5575 22.1299 16.7227 22.751 15.7575 22.751Z" stroke="#ACD602" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        </button>
                    )}

                    <div className="flex items-center gap-4 pr-3">
                        <div className="relative flex h-28 w-28 items-center justify-center">
                            <svg className="h-full w-full" viewBox="0 0 36 36">
                                <path
                                    className="stroke-gray-100"
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    strokeWidth="2"
                                />
                                <path
                                    className="stroke-[#BADA55]"
                                    strokeDasharray={`${scoreStrokePercentage}, 100`}
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    strokeWidth="2"
                                    strokeLinecap="square"
                                />
                            </svg>
                            <span className="absolute text-xl font-bold text-gray-700">{scorePercentage.toFixed(0)}%</span>
                        </div>
                    </div>
                </div>
            </div>

            {showTabs && (
                <div className="mt-6 flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => setActiveTab("overview")}
                        className={tabButtonClass(activeTab === "overview")}
                    >
                        Full Profile
                    </button>
                    {showFeedbackTab && (
                        <button
                            type="button"
                            onClick={() => setActiveTab("feedback")}
                            className={tabButtonClass(activeTab === "feedback")}
                        >
                            Feedback
                        </button>
                    )}
                </div>
            )}

            {/* Share profile modal */}
            {isShareModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeShareModal}>
                    <div
                        className="relative w-full max-w-md rounded-md bg-white p-6 shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={closeShareModal}
                            aria-label="Close share modal"
                            className="absolute right-4 top-4 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                        >
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        <h2 className="mb-6 text-lg font-bold text-gray-900">
                            Share &apos;{shareTitle}&apos;
                        </h2>

                        <div className="mb-6">
                            <div className="flex gap-2">
                                <input
                                    type="email"
                                    placeholder="Add people by email"
                                    value={addPeopleInput}
                                    onChange={(e) => {
                                        setAddPeopleInput(e.target.value)
                                        if (emailError) setEmailError("")
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault()
                                            handleAddPerson()
                                        }
                                    }}
                                    className={`w-full rounded-md border bg-[#F9F9F9] px-4 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-1 ${
                                        emailError
                                            ? "border-red-400 focus:border-red-400 focus:ring-red-400"
                                            : "border-gray-300 focus:border-gray-400 focus:ring-gray-400"
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={handleAddPerson}
                                    className="btn-cta rounded-md px-4 py-3 text-sm font-semibold whitespace-nowrap"
                                >
                                    Add
                                </button>
                            </div>
                            {emailError && (
                                <p className="mt-1.5 text-xs text-red-500">{emailError}</p>
                            )}
                        </div>

                        <div className="mb-6">
                            <h3 className="mb-3 text-sm font-semibold text-gray-700">People with access</h3>
                            <ul className="space-y-3">
                                {peopleWithAccess.length === 0 ? (
                                    <li className="text-sm text-gray-500">No one has been added yet.</li>
                                ) : (
                                    peopleWithAccess.map((person, index) => (
                                        <li key={`${person.email}-${index}`} className="flex items-center gap-3">
                                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#38D04B] text-sm font-semibold text-white">
                                                {getInitial(person)}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium text-gray-900">{person.name || person.email.split("@")[0]}</p>
                                                <p className="truncate text-xs text-gray-500">{person.email}</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemovePerson(person.email)}
                                                aria-label={`Remove ${person.email}`}
                                                className="ml-auto flex-shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        </li>
                                    ))
                                )}
                            </ul>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={handleShareSave}
                                disabled={isSharingFeedback || peopleWithAccess.length === 0}
                                className="btn-cta rounded-md px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
                            >
                                {isSharingFeedback ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}